import { NOOP } from '@lemonade-hq/ts-helpers';
import type { PropsWithChildren } from 'react';
import React, { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';
import { Drawer } from '../components/Drawer/Drawer';
import { Layout } from '../components/Layout/Layout';
import { content } from '../components/Menu/Menu.css';
import { useViewportSize } from '../hooks/useViewportSize';
import { spacing } from '../styles/spacing.css';

export interface ContextMenuProps<TContextMenuProps = Record<string, unknown>> {
  readonly title?: string;
  readonly fullscreen?: boolean;
  readonly component: React.FC<TContextMenuProps>;
  readonly props: TContextMenuProps;
  readonly onOpenChange?: (open: boolean) => void;
}

interface ModalContextValue {
  readonly isOpen: boolean;
  readonly open: <TContextMenuProps = Record<string, unknown>>(props: ContextMenuProps<TContextMenuProps>) => void;
  readonly close: () => void;
}

const ModalContext = createContext<ModalContextValue>({
  isOpen: false,
  open: NOOP,
  close: NOOP,
});

interface IsomorphicContextMenuProps extends Omit<ContextMenuProps, 'component' | 'props'> {
  readonly position: ModalPosition;
  readonly open: boolean;
}

const IsomorphicContextMenu = forwardRef<HTMLDivElement, PropsWithChildren<IsomorphicContextMenuProps>>(
  ({ children, open, onOpenChange, fullscreen, position, ...modalOptions }, ref) => {
    const { isBelowDesktopViewport } = useViewportSize();
    const layoutRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (open && layoutRef.current) {
        const item: HTMLElement | null = document.querySelector('[data-wizard-menu-item="wizard-menu-item-1"]');
        item?.focus();
      }
    }, [open]);

    if (isBelowDesktopViewport) {
      return (
        <Drawer.Root onOpenChange={onOpenChange} open={open} {...modalOptions} dismissible={!fullscreen}>
          <Drawer.Content
            description={modalOptions.title ?? 'Context Menu'}
            fullscreen={fullscreen}
            title={modalOptions.title ?? 'Context Menu'}
          >
            {children}
          </Drawer.Content>
        </Drawer.Root>
      );
    }

    if (!open) return null;

    return (
      <Layout
        className={content}
        left={position.x}
        position="absolute"
        ref={mergeRefs([ref, layoutRef])}
        style={{ transform: `translate(${spacing.s16}, -85%)` }}
        top={position.y}
      >
        {children}
      </Layout>
    );
  },
);

interface ModalPosition {
  readonly x: string;
  readonly y: string;
}

export const ContextMenuProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [modalPosition, setModalPosition] = useState<ModalPosition>({ x: '0', y: '0' });
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  const handleSetModalPosition = useCallback(() => {
    const activeElement = document.activeElement;
    /* istanbul ignore if -- @preserve - can't mock null ref */
    if (activeElement == null) return;

    const { right, bottom } = activeElement.getBoundingClientRect();

    const modalPositionX = `calc(calc(${right}px) + ${document.documentElement.scrollLeft}px)`;
    const modalPositionY = `calc(calc(${bottom}px) + ${document.documentElement.scrollTop}px)`;

    setModalPosition({
      x: modalPositionX,
      y: modalPositionY,
    });
  }, [setModalPosition]);

  const handleClick = useCallback(
    (event: MouseEvent): void => {
      if (contextMenuRef.current != null && event.target instanceof HTMLElement) {
        /* istanbul ignore if -- @preserve - can't mock null ref */
        if (!contextMenuRef.current.contains(event.target)) {
          setIsOpen(prev => !prev);
        }
      }

      handleSetModalPosition();
    },
    [setIsOpen, handleSetModalPosition],
  );

  const handleKeyPressed = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
      if (event.key === 'Enter') handleSetModalPosition();
    },
    [setIsOpen, handleSetModalPosition],
  );

  useEffect(() => {
    document.body.addEventListener('click', handleClick, true);
    document.body.addEventListener('keydown', handleKeyPressed, true);

    return () => {
      document.body.removeEventListener('click', handleClick, true);
      document.body.removeEventListener('keydown', handleKeyPressed, true);
    };
  }, [handleClick, handleKeyPressed]);

  const [{ component: Component, props, onOpenChange, ...dialogOptions }, setContextMenu] = useState<ContextMenuProps>({
    component: /* istanbul ignore next -- not covered even if accessed for some reason */ () => null,
    props: { onClose: NOOP },
  });

  const onChange = useCallback(
    (newState: boolean) => {
      onOpenChange?.(newState);
      setIsOpen(newState);
    },
    [onOpenChange],
  );

  const close = useCallback(() => {
    onChange(false);
  }, [onChange]);

  const open = useCallback(
    <TModalProps extends Record<string, unknown> = Record<string, unknown>>(options: ContextMenuProps<TModalProps>) => {
      setContextMenu(options as unknown as ContextMenuProps);
      onChange(true);
    },
    [onChange],
  );

  const contextValue = useMemo(() => ({ isOpen, open, close }), [isOpen, open, close]);

  return (
    <ModalContext.Provider value={contextValue as ModalContextValue}>
      {children}
      <IsomorphicContextMenu
        ref={contextMenuRef}
        {...dialogOptions}
        onOpenChange={onChange}
        open={isOpen}
        position={modalPosition}
      >
        <Component {...props} />
      </IsomorphicContextMenu>
    </ModalContext.Provider>
  );
};

export function useContextMenuContext(): ModalContextValue {
  return useContext(ModalContext);
}
